export type AuctionDivision = "men" | "women";

export interface AuctionRuleSet {
  division: AuctionDivision;
  label: string;
  squadMin: number;
  squadMax: number;
  purseMin: number;
  purseMax: number;
  reserveForExtras: number;
  kittyAllocation: number;
  extraPlayerBaseBid: number;
  basePrices: Record<string, number>;
  minCounts: Record<string, number>;
  maxCounts: Record<string, number>;
  categoryLabels: string[];
}

const menBasePrices = {
  "Icon Player": 2000000,
  "Senior Player": 500000,
  "Emerging Player": 300000,
  "Development Player": 200000,
};

const menMinCounts = {
  "Icon Player": 1,
  "Senior Player": 3,
  "Emerging Player": 3,
  "Development Player": 5,
};

const menMaxCounts = {
  "Icon Player": 1,
  "Senior Player": 20,
  "Emerging Player": 20,
  "Development Player": 20,
};

const womenBasePrices = {
  "Icon Player": 500000,
  "Under-19 Player": 100000,
  "General Player": 200000,
  "Senior Player": 200000,
  "Emerging Player": 100000,
  "Development Player": 200000,
};

const womenMinCounts = {
  "Icon Player": 1,
  "Under-19 Player": 2,
  "General Player": 0,
  "Senior Player": 0,
  "Emerging Player": 0,
  "Development Player": 0,
};

const womenMaxCounts = {
  "Icon Player": 1,
  "Under-19 Player": 18,
  "General Player": 18,
  "Senior Player": 18,
  "Emerging Player": 18,
  "Development Player": 18,
};

export const AUCTION_RULES: Record<AuctionDivision, AuctionRuleSet> = {
  men: {
    division: "men",
    label: "Men's T20 Mumbai League 2026",
    squadMin: 18,
    squadMax: 20,
    purseMin: 8000000,
    purseMax: 10000000,
    reserveForExtras: 600000,
    kittyAllocation: 2900000,
    extraPlayerBaseBid: 200000,
    basePrices: menBasePrices,
    minCounts: menMinCounts,
    maxCounts: menMaxCounts,
    categoryLabels: ["Icon", "Senior", "Emerging", "Development"],
  },
  women: {
    division: "women",
    label: "Women's T20 Mumbai League 2026",
    squadMin: 16,
    squadMax: 18,
    purseMin: 4000000,
    purseMax: 5000000,
    reserveForExtras: 400000,
    kittyAllocation: 600000,
    extraPlayerBaseBid: 200000,
    basePrices: womenBasePrices,
    minCounts: womenMinCounts,
    maxCounts: womenMaxCounts,
    categoryLabels: ["Icon", "Under-19", "General"],
  },
};

export const resolveAuctionDivision = (
  gender?: string | null,
): AuctionDivision => {
  if (gender?.toLowerCase() === "female") {
    return "women";
  }
  return "men";
};

export const getAuctionRules = (gender?: string | null): AuctionRuleSet =>
  AUCTION_RULES[resolveAuctionDivision(gender)];

export const getAuctionRulesByDivision = (
  division: AuctionDivision,
): AuctionRuleSet => AUCTION_RULES[division];

export const normalizeCategoryForRules = (
  category: string,
  rules: AuctionRuleSet,
  playerContext?: {
    playerAge?: number | string | null;
    birthData?: string | null;
  },
): string => {
  if (!category) return category;
  if (rules.division !== "women") return category;

  if (category === "Icon Player") {
    return "Icon Player";
  }

  const ageValue =
    typeof playerContext?.playerAge === "number"
      ? playerContext.playerAge
      : typeof playerContext?.playerAge === "string"
        ? Number(playerContext.playerAge)
        : NaN;

  const hasValidAge = Number.isFinite(ageValue);
  if (hasValidAge && ageValue < 19) {
    return "Under-19 Player";
  }

  if (!hasValidAge && playerContext?.birthData) {
    const dob = new Date(playerContext.birthData);
    if (!Number.isNaN(dob.getTime())) {
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();
      const dayDiff = today.getDate() - dob.getDate();
      if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        age -= 1;
      }
      if (age < 19) {
        return "Under-19 Player";
      }
    }
  }

  return "General Player";
};

export const parseBasePriceToLakhs = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;

  if (typeof value === "number" && Number.isFinite(value)) {
    return value >= 100000 ? value / 100000 : value;
  }

  const raw = String(value).trim().toLowerCase();
  if (!raw || raw === "-") return null;

  const numeric = Number(raw.replace(/[^0-9.]/g, ""));
  if (!Number.isFinite(numeric) || numeric <= 0) return null;

  if (raw.includes("cr")) return numeric * 100;
  if (raw.includes("lakh") || raw.includes("lac") || raw.includes(" l")) return numeric;
  if (raw.includes("thousand") || raw.includes("k")) return numeric / 100;

  return numeric >= 100000 ? numeric / 100000 : numeric;
};

export const resolveCategoryFromBasePrice = (
  division: AuctionDivision,
  basePriceValue: unknown,
): string | null => {
  const lakhs = parseBasePriceToLakhs(basePriceValue);
  if (!lakhs) return null;

  if (division === "women") {
    if (lakhs >= 5) return "Icon Player";
    if (lakhs >= 2) return "General Player";
    if (lakhs >= 1) return "General Player";
    return "General Player";
  }

  if (lakhs > 20) return "Icon Player";
  if (lakhs >= 5 && lakhs < 20) return "Senior Player";
  if (lakhs >= 3) return "Emerging Player";
  if (lakhs >= 2) return "Development Player";
  return "Development Player";
};

const MCA_CATEGORY_OVERRIDES: Record<string, string> = {
  M01562: "Senior Player",
  M00088: "Senior Player",
  MCAS0021: "Senior Player",
  M00115: "Senior Player",
  MCAA0043: "Senior Player",
};

const normalizeMcaNo = (mcaNo: unknown): string =>
  String(mcaNo || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");

export const resolveCategoryOverrideByMcaNo = (
  mcaNo: unknown,
): string | null => {
  const normalizedMcaNo = normalizeMcaNo(mcaNo);
  if (!normalizedMcaNo) return null;
  return MCA_CATEGORY_OVERRIDES[normalizedMcaNo] || null;
};

export const resolveCategoryWithFallback = ({
  mcaNo,
  fallbackCategory,
}: {
  division: AuctionDivision;
  basePrice?: unknown;
  fallbackCategory?: string;
  mcaNo?: unknown;
  playerName?: string;
  playerAge?: number | string | null;
  birthData?: string | null;
}): string => {
  const mcaCategoryOverride = resolveCategoryOverrideByMcaNo(mcaNo);
  if (mcaCategoryOverride) return mcaCategoryOverride;
  return String(fallbackCategory || "").trim();
};

const extractTeamDivisionHint = (team: Record<string, unknown>): string => {
  const keys = [
    "division",
    "Division",
    "divisionType",
    "Division_Type__c",
    "League_Type__c",
    "Category__c",
    "Gender__c",
    "gender",
    "teamType",
    "Team_Type__c",
    "Name",
  ];

  for (const key of keys) {
    const value = team[key];
    if (typeof value === "string" && value.trim()) {
      return value.toLowerCase();
    }
  }
  return "";
};

export const resolveTeamDivision = (
  team: Record<string, unknown> | null | undefined,
): AuctionDivision => {
  if (!team) return "men";
  const hint = extractTeamDivisionHint(team);
  if (hint.includes("women") || hint.includes("female") || hint.includes("girls")) {
    return "women";
  }
  return "men";
};

export const resolveAuctionDivisionFromHints = (params: {
  gender?: string | null;
  divisionHint?: AuctionDivision | null;
  team?: Record<string, unknown> | null;
}): AuctionDivision => {
  const { gender, divisionHint, team } = params;
  if (typeof gender === "string" && gender.trim()) {
    return resolveAuctionDivision(gender);
  }
  if (divisionHint === "women" || divisionHint === "men") {
    return divisionHint;
  }
  if (team) {
    return resolveTeamDivision(team);
  }
  return "men";
};
